import { Device, User } from "@fairfleet/geotab";
import {
  TachographDeviceInfo,
  TachographDriverRow,
  TachographFile,
  TachographManufacturer,
  TachographRow,
  TachographSummary,
  TachographSummaryFilter,
  TachographTimeline,
  TachographTab,
} from "../models/Tachograph";
import { isKnownMake, VuTechnicalData } from "./tachographDeviceInfo";
import { extractDriverCardNumber } from "./tachographDrivers";
import { getFileReferenceDate, getFileUploadDate, hasFileData } from "./tachographFiles";
import {
  deriveConnectivityStatus,
  deriveStatusFromReferenceDate,
} from "./tachographStatus";
import { buildTimeline } from "./tachographTimeline";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";

type TranslateCount = (key: string, options: { count: number }) => string;

type VehicleRowsParams = {
  devices: Device[];
  driverIdByDeviceId: Record<string, string | undefined>;
  driverById: Map<string, User>;
  fileByDeviceId: Record<string, TachographFile>;
  signalByDeviceId: Map<string, Date | null>;
  infoByDeviceId: Map<string, TachographDeviceInfo>;
  /** Make, generation and calibration from the newest vehicle file that carries them. */
  technicalByDeviceId: Map<string, VuTechnicalData>;
  formatManufacturer: (manufacturer: TachographManufacturer) => string;
  intervalDays: number;
  warningDays: number;
  language: string;
  now: Date;
  t: TranslateCount;
  /**
   * Which sources loaded: a failed one leaves its status unknown. Until the user's deadlines
   * (`policy`) loaded, neither status nor due dates are derived from the defaults.
   */
  known: { files: boolean; signals: boolean; policy: boolean };
};

/**
 * One row per vehicle that says something about its tachograph. Vehicles without a readout
 * and without a tachograph signal are left out — they have no tachograph to report on.
 */
export function buildVehicleRows({
  devices,
  driverIdByDeviceId,
  driverById,
  fileByDeviceId,
  signalByDeviceId,
  infoByDeviceId,
  technicalByDeviceId,
  formatManufacturer,
  intervalDays,
  warningDays,
  language,
  now,
  t,
  known,
}: VehicleRowsParams): TachographRow[] {
  const rows: TachographRow[] = [];

  for (const device of devices) {
    const latestFile = fileByDeviceId[device.id];
    const latestSignal = signalByDeviceId.get(device.id) ?? null;
    if (!hasFileData(latestFile) && !latestSignal) continue;

    const referenceDate = getFileReferenceDate(latestFile);
    // Without the readouts a missing one is not "overdue", without the signals no unit is "offline".
    const downloadStatus = !(known.files && known.policy)
      ? undefined
      : referenceDate === null && latestSignal
        ? "overdue"
        : deriveStatusFromReferenceDate(referenceDate, intervalDays, warningDays, now);

    const connectorStatus = known.signals ? deriveConnectivityStatus(latestSignal, now) : undefined;

    const driverId = driverIdByDeviceId[device.id];
    const driver = driverId ? driverById.get(driverId) : undefined;
    const timeline = buildTimeline(
      known.policy ? referenceDate : null,
      getFileUploadDate(latestFile),
      intervalDays,
      language,
      now,
      t,
    );

    const info = infoByDeviceId.get(device.id);
    const technical = technicalByDeviceId.get(device.id);
    // The reading wins where it names a make; the file fills in what the tachograph never sent.
    const make = isKnownMake(info?.manufacturer) ? info?.manufacturer : (technical?.manufacturer ?? info?.manufacturer);
    const makeLabel = make ? formatManufacturer(make) : (technical?.manufacturerName ?? "");

    rows.push({
      id: device.id,
      summary: device.name,
      driver: getUserDisplayName(driver, "lastFirst"),
      licensePlate: getDeviceLicensePlate(device),
      vin: device.vehicleIdentificationNumber ?? "",
      serialNumber: device.serialNumber ?? "",
      driverId,
      manufacturer: [makeLabel, technical?.generation].filter(Boolean).join(" "),
      ...timeline,
      calibrationExpiryDate: info?.nextCalibration ?? technical?.nextCalibration ?? null,
      remoteDownload: info?.remoteDownload,
      downloadTaskStatus: info?.downloadTaskStatus,
      tachographStatus: info?.tachographStatus,
      downloadStatus,
      connectorStatus,
    });
  }

  return rows;
}

/**
 * The vehicle rows a tab shows. The Connectors tab lists every vehicle whose tachograph is not
 * online, whatever its download status, so it matches the counter above the table. The counter
 * filter narrows the status tab only.
 */
export function selectVehicleRows(
  rows: TachographRow[],
  tab: TachographTab,
  summaryFilter: TachographSummaryFilter,
): TachographRow[] {
  if (tab === "connectors") return rows.filter(isConnectorIssue);
  if (tab !== "status") return rows;

  switch (summaryFilter) {
    case "downloads":
      return rows.filter(isDownloadDue);
    case "connectors":
      return rows.filter(isConnectorIssue);
    default:
      return rows;
  }
}

/** An unknown status (its source failed) is no issue to count; the error alert says why. */
const isDownloadDue = (row: TachographRow) =>
  row.downloadStatus !== undefined && row.downloadStatus !== "ok";
const isConnectorIssue = (row: TachographRow) =>
  row.connectorStatus !== undefined && row.connectorStatus !== "online";

/**
 * The counters above the table. Each is null while a source it depends on is loading or failed
 * (a vehicle is a row for its readout or its signal, so a missing source would undercount).
 */
export function summarizeVehicleRows(
  rows: TachographRow[],
  known: Record<keyof TachographSummary, boolean>,
): TachographSummary {
  return {
    total: known.total ? rows.length : null,
    downloadsDue: known.downloadsDue ? rows.filter(isDownloadDue).length : null,
    connectorsIssue: known.connectorsIssue ? rows.filter(isConnectorIssue).length : null,
  };
}

/** A driver on a reporting vehicle without any card readout in the file window is overdue, like the vehicle rows. */
function buildNoReadoutTimeline(
  updateDate: Date | null,
  language: string,
  now: Date,
  t: TranslateCount,
): TachographTimeline {
  const text = t("tachograph.noReadout", { count: 0 });

  return {
    ...buildTimeline(null, updateDate, 0, language, now, t),
    legalLimit: text,
    nextDownloadUtc: text,
  };
}

type DriverRowsParams = {
  drivers: User[];
  deviceIdByDriverId: Map<string, string>;
  licensePlateByDeviceId: Map<string, string>;
  fileByDriverId: Record<string, TachographFile>;
  signalByDeviceId: Map<string, Date | null>;
  intervalDays: number;
  warningDays: number;
  language: string;
  now: Date;
  t: TranslateCount;
  /**
   * Which sources loaded: without the card readouts a missing one says nothing about the driver,
   * until the user's deadlines (`policy`) loaded no due date is derived from the defaults.
   */
  known: { files: boolean; policy: boolean };
};

/** One row per driver with a card readout or with a vehicle that reports a tachograph. */
export function buildDriverRows({
  drivers,
  deviceIdByDriverId,
  licensePlateByDeviceId,
  fileByDriverId,
  signalByDeviceId,
  intervalDays,
  warningDays,
  language,
  now,
  t,
  known,
}: DriverRowsParams): TachographDriverRow[] {
  const rows: TachographDriverRow[] = [];

  for (const driver of drivers) {
    const assignedDeviceId = deviceIdByDriverId.get(driver.id);
    const latestFile = fileByDriverId[driver.id];
    const hasSignal = !!assignedDeviceId && !!signalByDeviceId.get(assignedDeviceId);
    if (!hasFileData(latestFile) && !hasSignal) continue;

    const referenceDate = getFileReferenceDate(latestFile);

    rows.push({
      id: driver.id,
      driver: getUserDisplayName(driver, "lastFirst"),
      driverCardNumber: extractDriverCardNumber(driver),
      licensePlate: assignedDeviceId ? (licensePlateByDeviceId.get(assignedDeviceId) ?? "") : "",
      noReadout: known.files && !referenceDate,
      downloadStatus: !(known.files && known.policy)
        ? undefined
        : referenceDate === null
          ? "overdue"
          : deriveStatusFromReferenceDate(referenceDate, intervalDays, warningDays, now),
      ...(referenceDate || !known.files
        ? buildTimeline(
            known.policy ? referenceDate : null,
            getFileUploadDate(latestFile),
            intervalDays,
            language,
            now,
            t,
          )
        : buildNoReadoutTimeline(getFileUploadDate(latestFile), language, now, t)),
    });
  }

  return rows;
}
